import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
  TextField,
  FormHelperText,
  Select,
  Divider,
  useTheme,
  CircularProgress,
  styled,
} from '@mui/material';
import { format as dateFnsFormat } from 'date-fns';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useSetRecoilState, useRecoilValue } from 'recoil';

import {
  getProducts,
  getAttributes,
  updateProducts,
  updateVariants,
} from '@app/adapter/catalog-service';
import { useNavigateAdminDelegatable as useNavigate } from '@app/administrator/utils/useNavigateAdminDelegatable';
import { BulkFormControlItem } from '@app/components/Catalog/BulkFormControlItem';
import { WorkLocationFields } from '@app/components/Catalog/WorkLocationFields';
import { WorkLocationOtherField } from '@app/components/Catalog/WorkLocationOtherField';
import { ProductLayout } from '@app/components/Layout/ProductLayout';
import {
  AttributeSelect,
  AttributeSelectItem,
} from '@app/components/Shared/AttributeSelect';
import { DateField } from '@app/components/Shared/DateField';
import { NumberField } from '@app/components/Shared/NumberField';
import { TimeSelect } from '@app/components/Shared/TimeSelect';
import { snackbarOpenState, snackbarTextState } from '@app/domain/app';
import { organizationSelector } from '@app/domain/organization';
import {
  ProductBulkForm,
  productBulkFormSchema,
  defaultProductBulkForm,
} from '@app/schemas/catalog';
import { Product, ProductUpdate } from '@app/types/catalog';
import { convertNewLine } from '@app/utils';
import {
  fromOverWorkConditions,
  getCalculatedDairyPay,
  getCalculatedHourlyPay,
  toOverWorkConditions,
  workLocationMixKey,
} from '@app/utils/catalog';
import { getMenuItems } from '@app/utils/components';
import {
  productStatusList,
  productCommutingList,
  productVariantsSkuList,
  productSelectionList,
  productCategoryList,
  notRequireClinicalDepartmentName,
} from '@app/utils/constants';
import { restoreSanitizedString } from '@app/utils/format';
import {
  getRestoredProductWorkLocationValues,
  toWorkLocationCustomFields,
  toWorkLocationIds,
} from '@app/utils/useSyncProductWorkLocation';

const ColBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const RowBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
});

type MixedValues = {
  additionalInformation?: boolean;
  attributesClinical?: boolean;
  attributesJob?: boolean;
  breakTime?: boolean;
  catchphrase?: boolean;
  commuting?: boolean;
  count?: boolean;
  description?: boolean;
  orderConditions?: boolean;
  period?: boolean;
  price?: boolean;
  selection?: boolean;
  status?: boolean;
  time?: boolean;
  variantsDescription?: boolean;
  workLocation?: boolean;
};

export interface ProductBulkEditState {
  ids?: string[];
}

export function ProductBulkEdit() {
  const location = useLocation();
  const navigate = useNavigate();
  const organizationState = useRecoilValue(organizationSelector);
  const setSnackbarOpen = useSetRecoilState(snackbarOpenState);
  const setSnackbarText = useSetRecoilState(snackbarTextState);
  const locationState = location.state as ProductBulkEditState | null;
  const theme = useTheme();
  // 診療科目リストの状態
  const [clinicalDepartmentList, setClinicalDepartmentList] = useState<
    AttributeSelectItem[] | undefined
  >(undefined);
  const notRequireDepartmentId = useMemo(
    () =>
      clinicalDepartmentList
        ?.filter(
          (clinical) => clinical.name === notRequireClinicalDepartmentName
        )
        .map((cli) => cli.id) || [],
    [clinicalDepartmentList]
  );

  // 仕事種別リストの状態
  const [jobTypeList, setJobTypeList] = useState<
    AttributeSelectItem[] | undefined
  >(undefined);
  // 各求人データの混在有無の状態
  const [mixedValues, setMixedValues] = useState<MixedValues>({});
  // データ読み込み済みかどうかの状態
  const [isLoadData, setIsLoadData] = useState(false);
  // データを登録中かどうかの状態
  const [isLoadingRegister, setIsLoadingRegister] = useState(false);
  const [products, setProducts] = useState([] as Product[]);

  const commonClinicalIds = useMemo(() => {
    return (
      products[0]?.attributes
        ?.filter((a) =>
          clinicalDepartmentList?.some((i) => i.id === a.attributeId)
        )
        .map((a) => a.attributeId) ?? []
    );
  }, [clinicalDepartmentList, products]);
  const { control, handleSubmit, watch, reset, setValue, formState } =
    useForm<ProductBulkForm>({
      // NOTE:組織情報はRecoil初期値から後で差し替わるため、勤務地情報の初期値はloadProductsのresetで設定する
      defaultValues: defaultProductBulkForm,
      mode: 'onChange',
      resolver: zodResolver(productBulkFormSchema),
    });

  /** APIに渡すための求人データに変換 */
  const convertProduct = useCallback(
    (formValue: ProductBulkForm) => {
      const data: ProductUpdate = {
        ...(formValue.checkPeriod || formValue.checkStatus
          ? {
              publication: {
                ...(formValue.checkPeriod
                  ? {
                      since: new Date(formValue.startPeriod).toISOString(),
                      until: new Date(formValue.endPeriod).toISOString(),
                    }
                  : {}),
                ...(formValue.checkStatus
                  ? { status: formValue.status || 'ACTIVE' }
                  : {}),
              },
            }
          : {}),
        ...(formValue.checkWorkLocation
          ? {
              locationIds: toWorkLocationIds(formValue, organizationState),
            }
          : {}),
        ...(formValue.checkAttributesClinical || formValue.checkAttributesJob
          ? {
              attributes: [
                ...(formValue.checkAttributesClinical
                  ? formValue.attributesClinical.map((id) => ({
                      attributeId: id,
                      value:
                        clinicalDepartmentList?.find((v) => v.id === id)
                          ?.name ?? '',
                    }))
                  : []),
                ...(formValue.checkAttributesJob
                  ? formValue.attributesJob.map((id) => ({
                      attributeId: id,
                      value: jobTypeList?.find((v) => v.id === id)?.name ?? '',
                    }))
                  : []),
              ],
            }
          : {}),
        ...(formValue.checkDescription
          ? {
              description: formValue.description,
            }
          : {}),
        ...(formValue.checkAdditionalInformation
          ? { additionalInformation: formValue.additionalInformation }
          : {}),
        ...(formValue.checkPrice || formValue.checkVariantsDescription
          ? {
              variants: [
                {
                  ...(formValue.checkPrice
                    ? {
                        price: {
                          amount: formValue.variantsPriceAmount ?? 0,
                          currency: 'JPY',
                          taxIncluded: true,
                        },
                        sku: formValue.variantsSku || 'day',
                      }
                    : undefined),
                  ...(formValue.checkVariantsDescription
                    ? {
                        description: formValue.variantsDescription,
                      }
                    : undefined),
                },
              ],
            }
          : {}),
        customFields: {
          ...(formValue.checkOrderConditions
            ? {
                orderConditions: formValue.orderConditions,
              }
            : {}),
          ...(formValue.checkSelection
            ? {
                selection: formValue.selection,
              }
            : {}),
          ...(formValue.checkTime
            ? {
                endTime: formValue.endTime,
                overWorkConditions: toOverWorkConditions(formValue),
                startTime: formValue.startTime,
              }
            : {}),
          ...(formValue.checkBreakTime
            ? {
                breakTime: formValue.breakTimeNoParticular
                  ? 0
                  : formValue.breakTime ?? 0,
                breakTimeNoParticular: formValue.breakTimeNoParticular,
              }
            : {}),
          ...(formValue.checkCommuting
            ? {
                commuting: formValue.commuting,
                commutingCost:
                  formValue.commuting === 'UpperLimit' ||
                  formValue.commuting === 'FixedFee' ||
                  formValue.commuting === 'Include'
                    ? formValue.commutingCost ?? 0
                    : 0,
              }
            : {}),
          ...(formValue.checkWorkLocation
            ? {
                ...toWorkLocationCustomFields(formValue, organizationState),
              }
            : {}),
          ...(formValue.checkCount
            ? {
                count: formValue.count ?? 0,
              }
            : {}),
          ...(formValue.checkCatchphrase
            ? {
                catchphrase: formValue.catchphrase,
              }
            : {}),
          ...(formValue.checkVariantsDescription
            ? {
                incentive: formValue.variantsDescription,
              }
            : {}),
        },
      };
      return data;
    },
    [organizationState, jobTypeList, clinicalDepartmentList]
  );

  const handleRegisterButtonClick: Parameters<typeof handleSubmit>[0] =
    useCallback(
      async (formValue) => {
        if (!locationState?.ids?.length) {
          return;
        }
        try {
          setIsLoadingRegister(true);
          const data = convertProduct(formValue);
          const result = await updateProducts(
            products.map((product) => {
              const variantsSku =
                data.variants?.[data.variants.length - 1].sku ??
                product.variants[product.variants.length - 1].sku;
              const variantsPriceAmount =
                data.variants?.[data.variants.length - 1].price?.amount ??
                product.variants[product.variants.length - 1].price.amount;
              const startTime =
                data.customFields?.startTime ?? product.customFields.startTime;
              const endTime =
                data.customFields?.endTime ?? product.customFields.endTime;
              const breakTime =
                data.customFields?.breakTime ?? product.customFields.breakTime;
              return {
                data: {
                  ...data,
                  customFields: {
                    ...data.customFields,
                    calculatedDairyPay:
                      variantsSku === 'day'
                        ? variantsPriceAmount // 日給の場合は報酬金額そのまま設定
                        : getCalculatedDairyPay(
                            startTime,
                            endTime,
                            breakTime,
                            variantsPriceAmount
                          ), // 時給の場合は報酬金額×勤務時間
                    calculatedHourlyPay:
                      variantsSku === 'hour'
                        ? variantsPriceAmount // 時給の場合は報酬金額そのまま設定
                        : getCalculatedHourlyPay(
                            startTime,
                            endTime,
                            breakTime,
                            variantsPriceAmount
                          ), // 日給の場合は報酬金額÷勤務時間
                  },
                },
                ids: [product.id],
              };
            }),
            organizationState.id
          );
          if (result.status !== 200) {
            throw new Error(`${result.status} ${result.statusText}`);
          }

          if (data.variants?.[0]) {
            const resultVariants = await updateVariants([
              {
                data: data.variants[0],
                ids: products.map((p) => p.variants[p.variants.length - 1].id),
              },
            ]);
            if (resultVariants.status !== 200) {
              throw new Error(
                `Variants ${resultVariants.status} ${resultVariants.statusText}`
              );
            }
          }

          navigate('/products/spot-regular');
        } catch (error) {
          setSnackbarText(
            `求人の更新に失敗しました, ${
              error instanceof Error ? error.message : error
            }`
          );
          setSnackbarOpen(true);
          return [[], []] as AttributeSelectItem[][];
        } finally {
          setIsLoadingRegister(false);
        }
      },
      [
        convertProduct,
        locationState,
        organizationState.id,
        products,
        navigate,
        setSnackbarText,
        setSnackbarOpen,
      ]
    );

  /** 診療科目または仕事種別の読み込み */
  const loadAttributes = useCallback(async () => {
    try {
      const result = await getAttributes({
        filter: {
          groupName: ['clinicalDepartment'],
        },
        pageNumber: 0,
        pageSize: 100,
      });
      if (result.status !== 200) {
        throw new Error(`${result.status} ${result.statusText}`);
      }
      const clinicalList: AttributeSelectItem[] = result.data.value.map(
        (a) => ({
          group: a.items?.[0].groupName ?? '',
          id: a.id,
          name: a.items?.[0].value ?? '',
        })
      );
      setClinicalDepartmentList(clinicalList);

      const resultJob = await getAttributes({
        filter: {
          groupName: ['jobType'],
        },
        pageNumber: 0,
        pageSize: 100,
      });
      if (resultJob.status !== 200) {
        throw new Error(`${result.status} ${result.statusText}`);
      }
      const jobList: AttributeSelectItem[] = resultJob.data.value.map((a) => ({
        id: a.id,
        name: a.name ?? '',
      }));
      setJobTypeList(jobList);

      return [clinicalList, jobList];
    } catch (error) {
      setSnackbarText(
        `診療科目または仕事種別の取得に失敗しました, ${
          error instanceof Error ? error.message : error
        }`
      );
      setSnackbarOpen(true);
      return [[], []] as AttributeSelectItem[][];
    }
  }, [setSnackbarText, setSnackbarOpen]);

  /** 求人データの読み込み */
  const loadProducts = useCallback(
    async (
      clinicalList: AttributeSelectItem[],
      jobList: AttributeSelectItem[]
    ) => {
      if (!locationState?.ids?.length) {
        return;
      }
      try {
        setIsLoadData(false);
        const result = await getProducts(organizationState.id, {
          ids: locationState.ids,
          order: 'createdAt desc',
          pageNumber: 0,
          pageSize: 100,
        });
        if (result.status !== 200) {
          throw new Error(`${result.status}`);
        }
        const productList = result.data.value;

        setProducts(productList);

        // データ混在をチェック
        const isMixed = <U,>(callbackfn: (value: Product) => U) => {
          return Array.from(new Set(productList.map(callbackfn))).length > 1;
        };
        const mix: MixedValues = {
          additionalInformation: isMixed((p) => p.additionalInformation),
          attributesClinical: isMixed((p) =>
            p.attributes
              ?.filter((a) => clinicalList.some((i) => i.id === a.attributeId))
              .map((a) => a.attributeId)
              .sort()
              .join(',')
          ),
          attributesJob: isMixed((p) =>
            p.attributes
              ?.filter((a) => jobList.some((i) => i.id === a.attributeId))
              .map((a) => a.attributeId)
              .sort()
              .join(',')
          ),
          breakTime: isMixed(
            (p) =>
              `${p.customFields?.breakTime ?? ''} ${
                p.customFields?.breakTimeNoParticular ?? ''
              }`
          ),
          catchphrase: isMixed((p) => p.customFields?.catchphrase ?? ''),
          commuting: isMixed(
            (p) =>
              `${p.customFields?.commuting ?? ''} ${
                p.customFields?.commutingCost ?? ''
              }`
          ),
          count: isMixed((p) => p.customFields?.count),
          description: isMixed((p) => p.description),
          orderConditions: isMixed((p) => p.customFields?.orderConditions),
          period: isMixed(
            (p) =>
              `${p.publication?.since ?? ''}～${p.publication?.until ?? ''}`
          ),
          price: isMixed(
            (p) =>
              `${p.variants[p.variants.length - 1]?.sku ?? ''} ${
                p.variants[p.variants.length - 1]?.price.amount ?? ''
              }`
          ),
          selection: isMixed((p) => p.customFields?.selection),
          status: isMixed((p) => p.publication?.status),
          time: isMixed(
            (p) =>
              `${p.customFields?.startTime ?? ''}～${
                p.customFields?.endTime ?? ''
              } ${p.customFields?.overWorkConditions?.join(' ') ?? ''}`
          ),
          variantsDescription: isMixed(
            (p) => p.variants[p.variants.length - 1]?.description
          ),
          workLocation: isMixed(workLocationMixKey),
        };
        setMixedValues(mix);

        // フォーム用の求人データを生成
        const product = productList[0];
        const {
          access,
          isOrgAgency,
          workAddress1,
          workAddress2,
          workCity,
          workLocationOther: isWorkLocationOther,
          workPostalCode,
          workPrefecture,
        } = getRestoredProductWorkLocationValues(product, organizationState, {
          isMixed: mix.workLocation,
        });
        const clinicalAtt = product.attributes?.filter((a) =>
          clinicalList.some((i) => i.id === a.attributeId)
        );
        const isNotRequireCli =
          clinicalAtt.length === 1 &&
          clinicalAtt[0].value === notRequireClinicalDepartmentName;
        const categoryEnum = (productCategoryList.find(
          (c) => c.name === product.category.name
        )?.id ?? '') as ProductBulkForm['categoryEnum'];
        const formValues: ProductBulkForm = {
          ...defaultProductBulkForm,
          access,
          additionalInformation: mix.additionalInformation
            ? ''
            : restoreSanitizedString(product.additionalInformation),
          attributesClinical:
            mix.attributesClinical || isNotRequireCli
              ? []
              : product.attributes
                  ?.filter((a) =>
                    clinicalList.some((i) => i.id === a.attributeId)
                  )
                  .map((a) => a.attributeId) ?? [],
          attributesJob: mix.attributesJob
            ? []
            : product.attributes
                ?.filter((a) => jobList.some((i) => i.id === a.attributeId))
                .map((a) => a.attributeId) ?? [],
          breakTime: mix.breakTime
            ? null
            : product.customFields?.breakTime ?? null,
          breakTimeNoParticular: mix.breakTime
            ? false
            : product.customFields?.breakTimeNoParticular ?? false,
          catchphrase: mix.catchphrase
            ? ''
            : product.customFields?.catchphrase ?? '',
          categoryEnum,
          commuting: mix.commuting
            ? ''
            : (product.customFields?.commuting as ProductBulkForm['commuting']),
          commutingCost: mix.commuting
            ? null
            : product.customFields?.commutingCost ?? null,
          count: mix.count ? null : product.customFields?.count ?? null,
          day: productList
            .map((p) =>
              p.customFields?.day ? new Date(p.customFields?.day) : undefined
            )
            .sort((a, b) => (a?.getTime() ?? 0) - (b?.getTime() ?? 0))[0],
          description: mix.description
            ? ''
            : restoreSanitizedString(product.description),
          endPeriod: mix.period
            ? ''
            : dateFnsFormat(new Date(product.publication.until), 'yyyy/MM/dd'),
          endTime: mix.time ? '' : product.customFields?.endTime ?? '',
          isNotRequireClinicalDepartment: mix.attributesClinical
            ? false
            : !!(
                product.attributes.length === 1 &&
                product.attributes[0].value === notRequireClinicalDepartmentName
              ),
          isOrgAgency,
          orderConditions: mix.orderConditions
            ? ''
            : convertNewLine(product.customFields?.orderConditions ?? ''),
          ...(mix.time
            ? {
                overtimeAllowance: false,
                possibleOvertime: false,
                possibleTimeAdjust: false,
              }
            : fromOverWorkConditions(product.customFields?.overWorkConditions)),
          selection: mix.selection ? '' : product.customFields?.selection ?? '',
          startPeriod: mix.period
            ? ''
            : dateFnsFormat(new Date(product.publication.since), 'yyyy/MM/dd'),
          startTime: mix.time ? '' : product.customFields?.startTime ?? '',
          status: mix.status ? '' : product.publication?.status ?? '',
          variantsDescription: mix.variantsDescription
            ? ''
            : convertNewLine(
                product.variants?.[product.variants.length - 1]?.description ??
                  ''
              ),
          variantsPriceAmount: mix.price
            ? null
            : product.variants?.[product.variants.length - 1]?.price.amount ??
              null,
          variantsSku: mix.price
            ? ''
            : ((product.variants?.[0]?.sku ??
                'day') as ProductBulkForm['variantsSku']),
          workAddress1,
          workAddress2,
          workCity,
          workLocationOther: isWorkLocationOther,
          workPostalCode,
          workPrefecture,
        };
        reset(formValues);
      } catch (error) {
        setSnackbarText(
          `求人の取得に失敗しました。, ${
            error instanceof Error ? error.message : error
          }`
        );
        setSnackbarOpen(true);
      } finally {
        setIsLoadData(true);
      }
    },
    [
      locationState?.ids,
      organizationState,
      reset,
      setSnackbarText,
      setSnackbarOpen,
    ]
  );

  useEffect(() => {
    void (async () => {
      const [clinicalList, jobList] = await loadAttributes();
      await loadProducts(clinicalList, jobList);
    })();
  }, [loadAttributes, loadProducts]);

  // 求人データが読み込めるまで内容を表示しない
  if (!isLoadData) {
    return (
      <ProductLayout sx={{ minWidth: 1000, px: 0 }}>
        <RowBox alignItems="center" gap={1} px={2}>
          <Typography variant="h5">一括編集</Typography>
        </RowBox>
        <Divider />
        <ColBox gap={1} px={2}>
          <Typography>
            編集対象の求人ID：{locationState?.ids?.join('、')}
          </Typography>
        </ColBox>
        <CircularProgress sx={{ m: 'auto' }} />
      </ProductLayout>
    );
  }

  return (
    <ProductLayout sx={{ minWidth: 1000, px: 0 }}>
      <RowBox alignItems="center" gap={1} px={2}>
        <Typography variant="h5">一括編集</Typography>
        <RowBox alignItems="center" gap={2} ml="auto">
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            size="small"
            sx={{ width: '8rem' }}
            loading={isLoadingRegister}
            onClick={handleSubmit(handleRegisterButtonClick)}
          >
            一括編集
          </LoadingButton>
        </RowBox>
      </RowBox>

      <Divider />

      <ColBox gap={1} px={2}>
        <Typography>
          編集対象の求人ID：{locationState?.ids?.join('、')}
        </Typography>
      </ColBox>

      <Divider />

      <ColBox px={2}>
        <ColBox gap={2} mb={5}>
          <Typography sx={{ color: theme.palette.grey[600] }}>
            一括で変更したい項目にチェックを入れてください。
          </Typography>
          <Typography sx={{ color: theme.palette.grey[600] }}>
            「データ混在」となっている箇所は、選択した求人の間でデータが異なる箇所です。一括編集で選択しなければ、元のデータのままとなります。
          </Typography>
        </ColBox>
        {formState.errors.checkAll?.message && (
          <Typography color={theme.customPalette.alert}>
            {formState.errors.checkAll.message}
          </Typography>
        )}
        <BulkFormControlItem
          title="掲載ステータス"
          required
          mixed={mixedValues.status}
          checkName="checkStatus"
          control={control}
          watch={watch}
        >
          <Controller
            name="status"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <Select
                  {...field}
                  error={!!error}
                  sx={{ bgcolor: theme.palette.common.white, width: '20rem' }}
                  displayEmpty
                  renderValue={(selected: string) => {
                    if (selected) {
                      return productStatusList.find((a) => a.id === selected)
                        ?.name;
                    }
                    return (
                      <span
                        style={{
                          color: theme.customPalette.lightGray,
                        }}
                      >
                        掲載ステータスを選択
                      </span>
                    );
                  }}
                >
                  <MenuItem value="" disabled>
                    掲載ステータスを選択
                  </MenuItem>
                  {getMenuItems(productStatusList)}
                </Select>
                {error ? (
                  <FormHelperText error sx={{ ml: 2 }}>
                    {error.message}
                  </FormHelperText>
                ) : undefined}
              </>
            )}
          />
        </BulkFormControlItem>
        <BulkFormControlItem
          title="掲載期間"
          required
          mixed={mixedValues.period}
          checkName="checkPeriod"
          control={control}
          watch={watch}
        >
          <RowBox alignItems={'baseline'} gap={1}>
            <Controller
              name="startPeriod"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DateField
                  {...field}
                  ref={undefined}
                  inputRef={field.ref}
                  format="yyyy/MM/dd"
                  error={!!error}
                  sx={{ width: '10rem' }}
                  InputProps={{ sx: { bgcolor: theme.palette.common.white } }}
                  helperText={error?.message}
                />
              )}
            />
            <Typography>～</Typography>
            <Controller
              name="endPeriod"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DateField
                  {...field}
                  ref={undefined}
                  inputRef={field.ref}
                  format="yyyy/MM/dd"
                  error={!!error}
                  sx={{ width: '10rem' }}
                  InputProps={{ sx: { bgcolor: theme.palette.common.white } }}
                  helperText={error?.message}
                />
              )}
            />
          </RowBox>
        </BulkFormControlItem>
        <BulkFormControlItem
          title="勤務地"
          required
          mixed={mixedValues.workLocation}
          checkName="checkWorkLocation"
          control={control}
          watch={watch}
        >
          <WorkLocationOtherField
            control={control}
            organization={organizationState}
            setValue={setValue}
            watch={watch}
          />
          <WorkLocationFields
            control={control}
            organization={organizationState}
            setValue={setValue}
            variant="bulk"
            watch={watch}
          />
        </BulkFormControlItem>
        <BulkFormControlItem
          title="キャッチコピー"
          required
          mixed={mixedValues.catchphrase}
          checkName="checkCatchphrase"
          control={control}
          watch={watch}
        >
          <Controller
            name="catchphrase"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                ref={undefined}
                inputRef={field.ref}
                error={!!error}
                sx={{ width: '25rem' }}
                InputProps={{
                  sx: { bgcolor: theme.palette.common.white },
                }}
                multiline
                rows={3}
                helperText={error?.message}
                placeholder={'例）駅近クリニックで週1日からの募集です！'}
                inputProps={{ maxLength: 50 }}
              />
            )}
          />
        </BulkFormControlItem>
        <BulkFormControlItem
          title="募集科目"
          required
          mixed={mixedValues.attributesClinical}
          checkName="checkAttributesClinical"
          control={control}
          watch={watch}
        >
          <Controller
            name="isNotRequireClinicalDepartment"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                {...field}
                checked={field.value}
                label={notRequireClinicalDepartmentName}
                sx={{ width: 'fit-content' }}
                control={<Checkbox />}
                onChange={(e, checked) => {
                  if (checked) {
                    setValue('attributesClinical', notRequireDepartmentId);
                  } else {
                    setValue(
                      'attributesClinical',
                      mixedValues.attributesClinical ? [] : commonClinicalIds
                    );
                  }
                  field.onChange(e, checked);
                }}
              />
            )}
          />
          {!watch('isNotRequireClinicalDepartment') && (
            <Controller
              name="attributesClinical"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <AttributeSelect
                  {...field}
                  notRequireDepartmentId={notRequireDepartmentId[0]}
                  label="科目を選択"
                  isGroup
                  list={clinicalDepartmentList}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          )}
        </BulkFormControlItem>

        <BulkFormControlItem
          title="仕事種別"
          required
          mixed={mixedValues.attributesJob}
          checkName="checkAttributesJob"
          control={control}
          watch={watch}
        >
          <Controller
            name="attributesJob"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <AttributeSelect
                {...field}
                label="仕事種別を選択"
                list={jobTypeList}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </BulkFormControlItem>
        <BulkFormControlItem
          title="仕事内容"
          required
          mixed={mixedValues.description}
          checkName="checkDescription"
          control={control}
          watch={watch}
        >
          <Controller
            name="description"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                ref={undefined}
                inputRef={field.ref} // NOTE:バリデーション時にフォーカスされる
                error={!!error}
                sx={{ width: '22rem' }}
                InputProps={{
                  sx: { bgcolor: theme.palette.common.white },
                }}
                multiline
                rows={2.5}
                placeholder="仕事内容を入力"
                helperText={error?.message}
              />
            )}
          />
        </BulkFormControlItem>
        <BulkFormControlItem
          title="応募条件"
          mixed={mixedValues.orderConditions}
          checkName="checkOrderConditions"
          control={control}
          watch={watch}
        >
          <Controller
            name="orderConditions"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                ref={undefined}
                inputRef={field.ref} // NOTE:バリデーション時にフォーカスされる
                error={!!error}
                sx={{ width: '22rem' }}
                InputProps={{
                  sx: { bgcolor: theme.palette.common.white },
                }}
                multiline
                rows={2.5}
                placeholder="応募条件を入力"
                helperText={error?.message}
              />
            )}
          />
        </BulkFormControlItem>
        {watch('categoryEnum') === 'regularPartTime' && (
          <BulkFormControlItem
            title="選考方法"
            mixed={mixedValues.selection}
            checkName="checkSelection"
            control={control}
            watch={watch}
          >
            <Controller
              name="selection"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Select
                    {...field}
                    error={!!error}
                    sx={{ width: '12rem' }}
                    inputProps={{
                      sx: { bgcolor: theme.palette.common.white },
                    }}
                    displayEmpty
                    renderValue={(selected: string) => {
                      if (selected) {
                        return selected;
                      }
                      return (
                        <span
                          style={{
                            color: theme.customPalette.lightGray,
                          }}
                        >
                          選考方法を選択
                        </span>
                      );
                    }}
                  >
                    {getMenuItems(productSelectionList)}
                  </Select>
                  {error ? (
                    <FormHelperText error sx={{ ml: 2 }}>
                      {error.message}
                    </FormHelperText>
                  ) : undefined}
                </>
              )}
            />
          </BulkFormControlItem>
        )}
        <BulkFormControlItem
          title="その他情報"
          mixed={mixedValues.additionalInformation}
          checkName="checkAdditionalInformation"
          control={control}
          watch={watch}
        >
          <Controller
            name="additionalInformation"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                ref={undefined}
                inputRef={field.ref} // NOTE:バリデーション時にフォーカスされる
                error={!!error}
                sx={{ width: '22rem' }}
                InputProps={{
                  sx: { bgcolor: theme.palette.common.white },
                }}
                multiline
                rows={2.5}
                placeholder="その他情報を入力"
                helperText={error?.message}
              />
            )}
          />
        </BulkFormControlItem>
        <BulkFormControlItem
          title="勤務時間"
          required
          mixed={mixedValues.time}
          checkName="checkTime"
          control={control}
          watch={watch}
        >
          <RowBox alignItems="baseline" gap={2}>
            <Controller
              name="startTime"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TimeSelect
                  {...field}
                  startTime="00:00"
                  endTime="23:55"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            <Typography>～</Typography>
            <Controller
              name="endTime"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TimeSelect
                  {...field}
                  startTime="00:00"
                  endTime="96:55"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </RowBox>
          <ColBox my={1} width="15rem">
            <Controller
              name="possibleTimeAdjust"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControlLabel
                  {...field}
                  checked={field.value}
                  control={<Checkbox />}
                  label="時間調整可"
                  sx={{ my: -0.5 }}
                />
              )}
            />
            <Controller
              name="possibleOvertime"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControlLabel
                  {...field}
                  checked={field.value}
                  control={<Checkbox />}
                  label="残業あり"
                  sx={{ my: -0.5 }}
                />
              )}
            />
            <Controller
              name="overtimeAllowance"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControlLabel
                  {...field}
                  checked={field.value}
                  disabled={!watch('possibleOvertime')}
                  control={<Checkbox />}
                  label="残業代の追加あり"
                  sx={{ my: -0.5 }}
                />
              )}
            />
          </ColBox>
        </BulkFormControlItem>
        <BulkFormControlItem
          title="休憩時間"
          required
          mixed={mixedValues.breakTime}
          checkName="checkBreakTime"
          control={control}
          watch={watch}
        >
          <Controller
            name="breakTimeNoParticular"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControlLabel
                {...field}
                checked={field.value}
                control={<Checkbox />}
                label="随時・なし"
                sx={{ my: -0.5 }}
              />
            )}
          />
          <RowBox alignItems="baseline" gap={2}>
            <Controller
              name="breakTime"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <NumberField
                  {...field}
                  ref={undefined}
                  inputRef={field.ref} // NOTE:バリデーション時にフォーカスされる
                  disabled={watch('breakTimeNoParticular') ?? false}
                  error={!!error}
                  sx={{ width: '10rem' }}
                  InputProps={{
                    sx: { bgcolor: theme.palette.common.white },
                  }}
                  placeholder="休憩時間を入力"
                  helperText={error?.message}
                />
              )}
            />
            <Typography>分</Typography>
          </RowBox>
        </BulkFormControlItem>
        <BulkFormControlItem
          title="給与"
          required
          mixed={mixedValues.price}
          checkName="checkPrice"
          control={control}
          watch={watch}
        >
          <RowBox alignItems="baseline" gap={2}>
            <Controller
              name="variantsSku"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  sx={{ width: '8rem' }}
                  inputProps={{
                    sx: { bgcolor: theme.palette.common.white },
                  }}
                  displayEmpty
                  renderValue={(selected: string) => {
                    if (selected) {
                      return productVariantsSkuList.find(
                        (a) => a.id === selected
                      )?.name;
                    }
                    return (
                      <span
                        style={{
                          color: theme.customPalette.lightGray,
                        }}
                      >
                        給与を選択
                      </span>
                    );
                  }}
                >
                  <MenuItem value="" disabled>
                    給与を選択
                  </MenuItem>
                  {getMenuItems(productVariantsSkuList)}
                </Select>
              )}
            />
            <Controller
              name="variantsPriceAmount"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <NumberField
                  {...field}
                  ref={undefined}
                  inputRef={field.ref} // NOTE:バリデーション時にフォーカスされる
                  error={!!error}
                  sx={{ width: '10rem' }}
                  InputProps={{
                    sx: { bgcolor: theme.palette.common.white },
                  }}
                  placeholder="金額を入力"
                  helperText={error?.message}
                />
              )}
            />
            <Typography>円</Typography>
          </RowBox>
        </BulkFormControlItem>
        <BulkFormControlItem
          title="インセンティブ（ある場合は具体的な条件をご記載ください）"
          mixed={mixedValues.variantsDescription}
          checkName="checkVariantsDescription"
          control={control}
          watch={watch}
        >
          <Controller
            name="variantsDescription"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                ref={undefined}
                inputRef={field.ref} // NOTE:バリデーション時にフォーカスされる
                error={!!error}
                sx={{ width: '22rem' }}
                InputProps={{
                  sx: { bgcolor: theme.palette.common.white },
                }}
                multiline
                rows={2.5}
                placeholder="入院1件5000円、救急車1件5000円"
                helperText={error?.message}
              />
            )}
          />
        </BulkFormControlItem>
        <BulkFormControlItem
          title="交通費"
          required
          mixed={mixedValues.commuting}
          checkName="checkCommuting"
          control={control}
          watch={watch}
        >
          <RowBox alignItems="baseline" gap={2}>
            <Controller
              name="commuting"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  sx={{ width: '15rem' }}
                  inputProps={{
                    sx: { bgcolor: theme.palette.common.white },
                  }}
                  displayEmpty
                  renderValue={(selected: string) => {
                    if (selected) {
                      return productCommutingList.find((a) => a.id === selected)
                        ?.name;
                    }
                    return (
                      <span
                        style={{
                          color: theme.customPalette.lightGray,
                        }}
                      >
                        交通費を選択
                      </span>
                    );
                  }}
                >
                  <MenuItem value="" disabled>
                    交通費を選択
                  </MenuItem>
                  {getMenuItems(productCommutingList)}
                </Select>
              )}
            />
            {(watch('commuting') === 'UpperLimit' ||
              watch('commuting') === 'FixedFee' ||
              watch('commuting') === 'Include') && (
              <>
                <Controller
                  name="commutingCost"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <NumberField
                      {...field}
                      ref={undefined}
                      inputRef={field.ref} // NOTE:バリデーション時にフォーカスされる
                      error={!!error}
                      sx={{
                        width: '10rem',
                      }}
                      InputProps={{
                        sx: { bgcolor: theme.palette.common.white },
                      }}
                      placeholder="金額を入力"
                      helperText={error?.message}
                    />
                  )}
                />
                <Typography>円</Typography>
              </>
            )}
          </RowBox>
        </BulkFormControlItem>
        <BulkFormControlItem
          title="募集人数"
          required
          mixed={mixedValues.count}
          checkName="checkCount"
          control={control}
          watch={watch}
        >
          <Controller
            name="count"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <NumberField
                {...field}
                ref={undefined}
                inputRef={field.ref} // NOTE:バリデーション時にフォーカスされる
                sx={{ width: '10rem' }}
                InputProps={{
                  sx: { bgcolor: theme.palette.common.white },
                }}
                placeholder="募集人数を入力"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </BulkFormControlItem>
      </ColBox>
    </ProductLayout>
  );
}
